import { cn } from "@arcadeai/design-system/lib/utils";
import { Check } from "lucide-react";
import type { ReactNode } from "react";

/**
 * One numbered step laid out as a band: the label + guidance live in a
 * fixed-width column on the left, the controls fill the rest of the width on
 * the right. On narrow screens the two stack.
 *
 * The numbered marker turns into a green check once the step is satisfied
 * (`complete`).
 */
export function Step({
  index,
  title,
  description,
  headingId,
  complete = false,
  optional = false,
  divider = true,
  children,
}: {
  index: number;
  title: string;
  description?: string;
  headingId?: string;
  complete?: boolean;
  optional?: boolean;
  /** Top border + spacing separating this step from the one above it. */
  divider?: boolean;
  children: ReactNode;
}) {
  return (
    <section
      className={cn(
        "grid grid-cols-1 gap-x-12 gap-y-4 xl:grid-cols-[minmax(200px,18rem)_minmax(0,1fr)]",
        divider && "border-border border-t pt-8"
      )}
    >
      <div className="space-y-1.5">
        <div className="flex items-center gap-2.5">
          <span
            aria-hidden
            className={cn(
              "flex size-6 shrink-0 items-center justify-center rounded-full font-semibold text-xs transition-colors duration-200 motion-safe:transition-transform motion-safe:data-[complete=true]:scale-105",
              complete
                ? "bg-success text-success-foreground"
                : "bg-muted text-muted-foreground"
            )}
            data-complete={complete}
          >
            {complete ? <Check className="size-3.5" /> : index}
          </span>
          <h2 className="font-semibold text-base" id={headingId}>
            {title}
          </h2>
          {optional ? (
            <span className="rounded-full bg-muted px-2 py-0.5 font-medium text-[11px] text-muted-foreground">
              Optional
            </span>
          ) : null}
        </div>
        {description ? (
          <p className="pl-[2.125rem] text-muted-foreground text-sm leading-snug">
            {description}
          </p>
        ) : null}
      </div>
      <div className="min-w-0">{children}</div>
    </section>
  );
}
